import { useField } from '@kizi/comparator-core';
import { Div, P } from '@kizi/core-components';
import { capitalise } from '@kizi/shared-components';
import React from 'react';
import { Field } from '..';
import { Banner, PRODUCT_TYPE /*, Banner*/ } from '../..';
import { ENERGY_TARIFF_TYPE } from '../../../funnels/energy';
import { formatPrice } from '../../../utils/global';
import { Price, getTariffsSummary } from '../../../utils/product';
import { getLabel } from '../../../utils/translations';
import { ListComp, ListGroup } from './List';
import { MoreInfoButton } from './MoreInfoButton';

const SINGLE_TARIFF_DECIMALS = 5;

interface RatesCompProps {
  price: Price;
  type?: string;
  data?: any;
  onButtonInfoClick: React.MouseEventHandler;
}

export interface Rates {
  [PRODUCT_TYPE.ELECTRICITY]: Field[];
  [PRODUCT_TYPE.GAS]: Field[];
}

type ElData = {
  [ENERGY_TARIFF_TYPE.HIGH]?: Object;
  [ENERGY_TARIFF_TYPE.LOW]?: Object;
  [ENERGY_TARIFF_TYPE.SINGLE]?: Object;
};

function getTariffLabelByType(type: string) {
  if (type === ENERGY_TARIFF_TYPE.HIGH) {
    return 'normaaltarief';
  } else if (type === ENERGY_TARIFF_TYPE.LOW) {
    return 'daltarief';
  } else {
    return 'tarief';
  }
}

export function getRateLabel(value: string, isDemand = false): string {
  return capitalise(`${isDemand ? 'teruglevertarief ' : ''}${value} per kWh`);
}

export function getRate(label: string, value: number, decimals = 2): Field {
  return {
    label,
    value: formatPrice(value, decimals),
  };
}

export function getElectricityRates(data: ElData): Field[][] {
  let demandRates: Field[] = [];
  let supplyRates: Field[] = [];

  for (const elRate of [
    ENERGY_TARIFF_TYPE.HIGH,
    ENERGY_TARIFF_TYPE.LOW,
    ENERGY_TARIFF_TYPE.SINGLE,
  ]) {
    const obj = data[elRate];

    for (const type in obj) {
      let isDemand = type === 'demand';

      for (const rate of obj[type]) {
        const { meterType, total } = rate;

        (isDemand ? demandRates : supplyRates).push(
          getRate(
            getRateLabel(getTariffLabelByType(meterType), !isDemand),
            total,
            SINGLE_TARIFF_DECIMALS,
          ),
        );
      }
    }
  }

  return [demandRates, supplyRates];
}

export function getGasRates(data): Field[] {
  let rates: Field[] = [];

  for (const gasRate in data) {
    const obj = data[gasRate].demand;
    for (const rate of obj) {
      rates.push(getRate('Tarief per m³', rate.total, SINGLE_TARIFF_DECIMALS));
    }
  }

  return rates;
}

export function getRates(price): Rates {
  const { breakdown } = price;
  const { tariffs } = breakdown;
  const { electricity, gas } = tariffs;

  let rates: { electricity: Field[]; gas: Field[] } = {
    electricity: [],
    gas: [],
  };

  const electricityRates = getElectricityRates(electricity);

  rates[PRODUCT_TYPE.ELECTRICITY] = [...electricityRates[0]];
  rates[PRODUCT_TYPE.GAS] = getGasRates(gas);

  const bb = breakdown.breakdown;

  for (const category in bb) {
    const categoryData = bb[category];

    if (Object.keys(categoryData).length) {
      const rows = categoryData['delivery']['items']['deliveryTotal']['rows'];

      for (const row of rows) {
        const { delivery, priceYear } = row;
        if (delivery === 'Vaste leveringskosten') {
          rates[category].push(getRate(`${delivery} per jaar`, priceYear));
        }
      }
    }
  }

  rates[PRODUCT_TYPE.ELECTRICITY] = [
    ...rates[PRODUCT_TYPE.ELECTRICITY],
    ...electricityRates[1],
  ];

  return rates;
}

export function getAllRates(price): Rates {
  const rates = getRates(price);

  const energyAndGasRates = {
    electricity: [
      {
        label: 'Normaaltarief per kWh',
        value: undefined,
      },
      {
        label: 'Daltarief per kWh',
        value: undefined,
      },
      {
        label: 'Vaste leveringskosten per jaar',
        value: undefined,
      },

      {
        label: 'Teruglevertarief normaaltarief per kWh',
        value: undefined,
      },
      {
        label: 'Teruglevertarief daltarief per kWh',
        value: undefined,
      },
      {
        label: 'Tarief per kWh',
        value: undefined,
      },
    ],
    gas: [
      {
        label: 'Tarief per m³',
        value: undefined,
      },
      {
        label: 'Vaste leveringskosten per jaar',
        value: undefined,
      },
    ],
  };

  for (const cat in energyAndGasRates) {
    if (rates[cat].length) {
      for (const index in energyAndGasRates[cat]) {
        if (
          energyAndGasRates[cat].some(
            (element) => element.label === rates[cat][index]?.label,
          )
        ) {
          const i = energyAndGasRates[cat].findIndex(
            (element) => element.label === rates[cat][index]?.label,
          );

          energyAndGasRates[cat][i]['value'] =
            rates[cat][index]?.value ?? undefined;
        }
      }
    }
  }
  return energyAndGasRates;
}

export function getListGroup(rates: Rates, label: string): ListGroup {
  return {
    leftFieldStyle: {
      width: '100%',
    },
    rightFieldStyle: {
      width: '50%',
    },
    title: capitalise(getLabel(label)),
    list: rates[label],
  };
}

export const PriceCapCeillingLabel = ({ text, ...rest }) => {
  const field = useField('overview.applyEnergyCeiling');
  const isUnderCeil = text.includes('plafond') && text.includes('onder');

  if (!text.includes('plafond')) {
    return null;
  }

  if (field?.value === 0) {
    return null;
  }

  return (
    <Div
      css={{ boxSizing: 'border-box' }}
      alignSelf={'flex-start'}
      minWidth="92px"
      p={1}
      borderRadius="32px"
      backgroundColor={isUnderCeil ? 'supplementary.2' : 'supplementary.1'}
      {...rest}
    >
      <P variant="bodyRegular" fontSize="inherit !important">
        {isUnderCeil ? 'Onder Plafond' : 'Boven Plafond'}
      </P>
    </Div>
  );
};

const RatesComp = ({
  type,
  price,
  onButtonInfoClick,
  data,
}: RatesCompProps) => {
  const electricitysupplyhighField = useField(
    'getUserInput.electricitysupplyhigh',
  );
  const electricitysupplylowField = useField(
    'getUserInput.electricitysupplylow',
  );

  const contractDuration = data.filters.contractDuration;
  const electricitySupplyHigh = electricitysupplyhighField?.value ?? 0;
  const electricitySupplyLow = electricitysupplylowField?.value ?? 0;

  const hasElectricitySupplyHigh = (electricitySupplyHigh as number) > 0;
  const hasElectricitySupplyLow = (electricitySupplyLow as number) > 0;

  const showInfoBanner =
    type !== 'energy' &&
    contractDuration >= 12 &&
    (hasElectricitySupplyHigh || hasElectricitySupplyLow);

  const infoBannerContent =
    contractDuration === 12
      ? 'Per 1 januari 2027 vervalt de salderingsregeling. Vanaf dat moment mag de teruggeleverde en verbruikte elektriciteit niet meer met elkaar verrekend worden. De terugleververgoeding van 0,0100 per kWh geldt tot 1 januari 2027, daarna wordt het tarief 0,05723 per kWh. Een indicatie van jouw terugleverkosten tot 1 januari 2027 zie je hieronder in het tariefoverzicht, na januari 2027 wordt het tarief €0,05223 per kWh incl. BTW.'
      : 'Per 1 januari 2027 vervalt de salderingsregeling. Vanaf dat moment mag de teruggeleverde en verbruikte elektriciteit niet meer met elkaar verrekend worden. De terugleververgoeding van 0,0100 per kWh geldt tot 1 januari 2027, daarna wordt het tarief 0,04478 per kWh. Een indicatie van jouw terugleverkosten tot 1 januari 2027 zie je hieronder in het tariefoverzicht, na januari 2027 wordt het tarief €0,03978 per kWh incl. BTW.';

  const { month, year, breakdown } = price;
  const { discountIncluded } = year;
  const { tariffSummary } = breakdown;

  const rates: Rates = getRates(price);
  let prices: Field[] = [];

  if (discountIncluded) {
    prices.push(
      getRate('Totaalbedrag per jaar', discountIncluded + year.total),
    );
    prices.push(getRate('Loyaliteitsbonus per jaar', discountIncluded));
  }

  let groups: ListGroup[] = [];

  if (rates[PRODUCT_TYPE.ELECTRICITY].length) {
    groups.push(getListGroup(rates, PRODUCT_TYPE.ELECTRICITY));
  }

  if (rates[PRODUCT_TYPE.GAS].length) {
    groups.push(getListGroup(rates, PRODUCT_TYPE.GAS));
  }

  const defaultCostsItem: Field = {
    bold: true,
    label: '',
    value: '',
  };

  const newGroup = getTariffsSummary(
    tariffSummary,
    'list',
    PriceCapCeillingLabel,
    { fontSize: '12px' },
    breakdown,
  );

  return (
    <>
      {showInfoBanner && (
        <Banner iconName="info" body={infoBannerContent} mb={3} />
      )}
      <Div display={[null, null, 'flex']}>
        <ListComp groups={newGroup} />
        <ListComp
          groups={[
            {
              disclaimer:
                type === 'energyAuction'
                  ? 'Hier zie je de geschatte kosten. In deze berekening nemen we alle kosten mee. Zo heb je een goed overzicht. Let op, je nieuwe energieleverancier kijkt naar het gemiddelde verbruik op het adres. Het uiteindelijke bedrag kan dus afwijken van onze schatting.'
                  : 'Hier zie je de geschatte kosten. In deze berekening nemen we alle kosten mee. Zo heb je een goed overzicht. Deze berekening is een schatting gebaseerd op de huidige marktprijzen. Omdat die bij een dynamisch contract steeds veranderen, kunnen de daadwerkelijke jaarkosten hoger of lager uitvallen. Let op, je nieuwe energieleverancier kijkt naar het gemiddelde verbruik op het adres. Het uiteindelijke bedrag kan dus afwijken van onze schatting.',
              title: 'Totaal',
              list: [
                ...prices,
                {
                  ...defaultCostsItem,
                  ...getRate(`Kosten per ${getLabel('year')}`, year.total),
                },
                {
                  ...defaultCostsItem,
                  ...getRate(`Kosten per ${getLabel('month')}`, month.total),
                },
              ],
            },
          ]}
        />
      </Div>
      <MoreInfoButton text="Toon alle tarieven" onClick={onButtonInfoClick} />
    </>
  );
};

export default RatesComp;
